import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";

// DMS pages
import WorkflowBuilder from "./pages/WorkflowBuilder.jsx";
import WorkflowRunner from "./pages/WorkflowRunner";
import WorkflowManagementPage from "./pages/WorkflowManagementPage";
import ProjectsPage from "./pages/ProjectsPage";
import ProjectDetailPage from "./pages/ProjectDetailPage";
import ManageProjectPage from "./pages/ManageProjectPage";
import StageWorkPage from "./pages/StageWorkPage";
import DashboardPage from "./pages/DashboardPage";
import MySchedulePage from "./pages/MySchedulePage";
import TeamActivityPage from "./pages/TeamActivityPage";
import OrgScheduleSettingsPage from "./pages/OrgScheduleSettingsPage";
import PlannerPage from "./pages/PlannerPage.jsx";
import InvoicingPage from "./pages/InvoicingPage.jsx";
import ArchivedProjectsPage from "./pages/ArchivedProjectsPage.jsx";
import TasksPage from "./pages/TasksPage.jsx";
import InboxPage from "./pages/InboxPage.jsx";

// Shared pages
import CompanyRegisterPage from "../shared/CompanyRegisterPage";
import LoginPage from "../shared/LoginPage";
import ForgotPasswordPage from "../shared/ForgotPasswordPage";
import SetPasswordPage from "../shared/SetPasswordPage";
import ChangePasswordPage from "../shared/ChangePasswordPage";
import UserManagementPage from "../shared/UserManagementPage";
import UserActivityReportPage from "../shared/UserActivityReportPage";
import TeamAttendanceReportPage from "../shared/TeamAttendanceReportPage";
import RoleManagementPage from "../shared/RoleManagementPage";
import CustomizationPage from "../shared/CustomizationPage.jsx";
import AuditLogPage from "../shared/AuditLogPage.jsx";
import ApiTokensPage from "../shared/ApiTokensPage.jsx";
import OAuthConsentPage from "../shared/OAuthConsentPage.jsx";
import SubscriptionPage from "../shared/SubscriptionPage.jsx";
import SuperAdminLoginPage from "../shared/SuperAdminLoginPage.jsx";
import SuperAdminDashboardPage from "../shared/SuperAdminDashboardPage.jsx";

// Layout & guards
import AppLayout from "../../components/layout/AppLayout";
import ProtectedRoute from "../../components/layout/ProtectedRoute";
import AdminRoute from "../../components/layout/AdminRoute";
import MustChangePasswordGuard from "../../components/layout/MustChangePasswordGuard";
import PermissionRoute from "../../components/layout/PermissionRoute";
import DefaultHomeRedirect from "../../components/layout/DefaultHomeRedirect";
import SuperAdminRoute from "../../components/layout/SuperAdminRoute.jsx";
import DMSRoute from "./DMSRoute";
import { PERMISSIONS } from "../../constants/permissions";

function DMSApp() {
  return (
    <Routes>
      {/* public */}
      <Route path="/register-company" element={<CompanyRegisterPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/set-password" element={<SetPasswordPage />} />
      <Route path="/superadmin/login" element={<SuperAdminLoginPage />} />
      <Route
        path="/superadmin"
        element={
          <SuperAdminRoute>
            <SuperAdminDashboardPage />
          </SuperAdminRoute>
        }
      />

      {/* protected */}
      <Route element={<ProtectedRoute />}>
        {/* Full-screen like the login page; the API checks admin and module. */}
        <Route path="/oauth/consent" element={<OAuthConsentPage />} />
        <Route element={<DMSRoute />}>
        <Route element={<MustChangePasswordGuard />}>
          {/* Subscription page lives inside AppLayout but outside the expiry gate */}
          <Route element={<AppLayout />}>
            <Route path="/subscription" element={<SubscriptionPage />} />
          </Route>
          <Route element={<AppLayout />}>
            <Route index element={<DefaultHomeRedirect />} />
            <Route
              element={
                <PermissionRoute
                  permission={PERMISSIONS.PROJECT_VIEW_ALL}
                />
              }
            >
              <Route path="/dashboard" element={<DashboardPage />} />
            </Route>
            <Route path="/projects" element={<ProjectsPage />} />
            <Route path="/projects/archived" element={<ArchivedProjectsPage />} />
            <Route
              element={
                <PermissionRoute permission={PERMISSIONS.INVOICING_VIEW} />
              }
            >
              <Route path="/invoicing" element={<InvoicingPage />} />
            </Route>
            <Route path="/projects/:id" element={<ProjectDetailPage />} />
            <Route
              path="/projects/:projectId/manage"
              element={<ManageProjectPage />}
            />
            <Route path="/planner" element={<PlannerPage />} />
            <Route
              element={<PermissionRoute permission={PERMISSIONS.TASK_VIEW} />}
            >
              <Route path="/tasks" element={<TasksPage />} />
              <Route path="/tasks/:taskId" element={<TasksPage />} />
            </Route>
            <Route path="/inbox" element={<InboxPage />} />
            <Route
              path="/projects/:id/stage/:assignmentId"
              element={<StageWorkPage />}
            />
            <Route
              element={
                <PermissionRoute
                  permission={PERMISSIONS.SCHEDULE_VIEW_OWN}
                />
              }
            >
              <Route path="/schedule" element={<MySchedulePage />} />
            </Route>
            <Route
              element={
                <PermissionRoute
                  permission={PERMISSIONS.TEAM_ACTIVITY_VIEW_OWN}
                />
              }
            >
              <Route path="/team" element={<TeamActivityPage />} />
            </Route>
            <Route
              element={
                <PermissionRoute
                  permission={PERMISSIONS.WORKFLOW_MANAGE_VIEW}
                />
              }
            >
              <Route
                path="/workflows"
                element={<WorkflowManagementPage />}
              />
            </Route>
            <Route
              element={
                <PermissionRoute
                  permission={PERMISSIONS.WORKFLOW_MANAGE_CREATE}
                />
              }
            >
              <Route
                path="/workflow-builder"
                element={<WorkflowBuilder />}
              />
              <Route
                path="/workflow-builder/:workflowId"
                element={<WorkflowBuilder />}
              />
            </Route>
            <Route
              element={
                <PermissionRoute
                  permission={PERMISSIONS.WORKFLOW_RUN_START}
                />
              }
            >
              <Route path="/workflow-runner" element={<WorkflowRunner />} />
              <Route
                path="/workflow-runner/:runId"
                element={<WorkflowRunner />}
              />
            </Route>
            <Route
              path="/change-password"
              element={<ChangePasswordPage />}
            />
            {/* Open to everyone: each user manages their own AI connections,
                admins see the whole workspace's. */}
            <Route path="/ai-access" element={<ApiTokensPage />} />
            <Route path="/admin/ai-access" element={<Navigate to="/ai-access" replace />} />
            <Route
              element={
                <PermissionRoute permission={PERMISSIONS.USER_VIEW_ALL} />
              }
            >
              <Route path="/admin/users" element={<UserManagementPage />} />
              <Route path="/admin/users/:userId/activity-report" element={<UserActivityReportPage />} />
              <Route path="/admin/users/attendance-report" element={<TeamAttendanceReportPage />} />
            </Route>
            <Route
              element={
                <PermissionRoute permission={PERMISSIONS.ROLE_VIEW} />
              }
            >
              <Route path="/admin/roles" element={<RoleManagementPage />} />
            </Route>
            <Route
              element={
                <PermissionRoute
                  permission={PERMISSIONS.WORKFLOW_MANAGE_VIEW}
                />
              }
            >
              <Route
                path="/admin/workflows"
                element={<WorkflowManagementPage />}
              />
            </Route>
            <Route element={<AdminRoute />}>
              <Route
                path="/admin/customizations"
                element={<CustomizationPage />}
              />
              {/* Audit trail is admin-only by design — never entitlement-based. */}
              <Route
                path="/admin/audit-logs"
                element={<AuditLogPage backTo="/dashboard" backLabel="Dashboard" />}
              />
            </Route>
            <Route
              element={
                <PermissionRoute
                  permission={PERMISSIONS.SCHEDULE_CONFIGURE_ORG}
                />
              }
            >
              <Route
                path="/admin/schedule-settings"
                element={<OrgScheduleSettingsPage />}
              />
            </Route>
          </Route>
        </Route>
        </Route>
      </Route>
    </Routes>
  );
}

export default DMSApp;
